import React, { useState } from 'react'
import { t } from 'i18next'
import {
    Box,
    FormHelperText,
    MenuItem,
} from '@material-ui/core'
import { MaskedInput } from '@crystalservice/crystals-ui/lib/components/inputs/masked-input/masked-input'
import { SelectInput } from '@crystalservice/crystals-ui/lib/components/inputs/select-input/select-input'
import { CountryFlag } from './country-flag'
import { phoneToServerValidValue, toMask } from '../../domain/client-masks'
import {
    changePhoneCountry,
    CountryCodeISO,
    getCountryCodeByPhone,
    intlPhoneSettings,
    phoneWithCountryCode,
    phoneWithoutCountryCode,
} from '../../utils/phone-utils'

const styles = require('./registration-page.module.scss')

interface IntlPhoneInputProps {
    countries: CountryCodeISO[]
    value: string
    error: boolean
    errorText?: string
    disabled?: boolean
    /**
     * Если указан, то в селекте будет принудительно выставлена эта страна
     */
    forceCountry?: CountryCodeISO
    onValueChange: (value: string) => void
    onCountryChange?: (countryCode: CountryCodeISO) => void
}

export const IntlPhoneInput: React.FC<IntlPhoneInputProps> = ({
    countries,
    value,
    error,
    errorText,
    disabled,
    forceCountry,
    onValueChange,
    onCountryChange,
}) => {
    const [selectedCountry, setSelectedCountry] = useState<CountryCodeISO>(() => {
        if (forceCountry) {
            return forceCountry
        }

        return value
            ? getCountryCodeByPhone(value, countries[0])
            : countries[0]
    })

    const handleCountryChange = (option): void => {
        onCountryChange && onCountryChange(option.value)

        if (value) {
            onValueChange(changePhoneCountry(value, option.value))
        }

        setSelectedCountry(option.value)
    }

    const handlePhoneChange = (newPhone: string): void => {
        const result = newPhone === '' || newPhone === null
            ? null
            : phoneToServerValidValue(phoneWithCountryCode(newPhone, selectedCountry))

        onValueChange(result)
    }

    return (
        <div id="intlPhoneInput">
            <Box display="flex">
                <Box width={115} mr={2}>
                    <SelectInput
                        id="intlPhoneInput-countrySelect"
                        className={styles.select}
                        endAdornment={<div/>}
                        value={selectedCountry}
                        onSelect={handleCountryChange}
                        options={countries.map(country => ({ value: country, label: country }))}
                        customOptionElements={countries.map(country => (
                            <MenuItem
                                key={country}
                                id={`intlPhoneInput-countrySelect-option-${country}`}
                                value={country}
                            >
                                <Box display="flex" alignItems="center">
                                    <CountryFlag country={country} />
                                    <Box ml={2} width={55}>
                                        {`+${intlPhoneSettings[country].code}`}
                                    </Box>
                                    <Box ml={2}>
                                        {t(`countries.iso.${country}`)}
                                    </Box>
                                </Box>
                            </MenuItem>
                        ))}
                        renderedValue={country => (
                            <Box display="flex" alignItems="center">
                                <CountryFlag country={country} />
                                <Box ml={2}>
                                    {`+${intlPhoneSettings[selectedCountry].code}`}
                                </Box>
                            </Box>
                        )}
                        disabled={disabled}
                    />
                </Box>

                <Box flexGrow={1}>
                    <MaskedInput
                        id="intlPhoneInput-numberInput"
                        className={styles.textField}
                        placeholder={intlPhoneSettings[selectedCountry].mask}
                        endAdornment={<div/>}
                        mask={toMask(intlPhoneSettings[selectedCountry].mask)}
                        autoComplete="off"
                        guide
                        placeholderChar="_"
                        value={value ? phoneWithoutCountryCode(value) : null}
                        error={error}
                        onValueChange={handlePhoneChange}
                        disabled={disabled}
                        variant="outlined"
                    />
                </Box>
            </Box>

            {Boolean(errorText) && (
                <Box style={{ margin: '3px 14px 0 14px' }}>
                    <FormHelperText
                        id="intlPhoneInput-helperText"
                        error
                        style={{ color: '#FA5724' }}
                    >
                        {errorText}
                    </FormHelperText>
                </Box>
            )}
        </div>
    )
}
